import React from 'react';
import { Box, Typography, Button, Skeleton } from '@mui/material';
import { Groups as TeamIcon } from '@mui/icons-material';
import type { IncidentAssignment } from '../../Types/IncidentAssignment';
import { formatTeamSummary } from '../../Utils/incidentTeamHelpers';
import { appColors } from '../../Config/Theme';

interface IncidentTeamSummaryProps {
  assignments: IncidentAssignment[];
  loading?: boolean;
  error?: unknown;
  onViewTeam: () => void;
}

const IncidentTeamSummary: React.FC<IncidentTeamSummaryProps> = ({
  assignments,
  loading,
  error,
  onViewTeam,
}) => {
  const activeAssignments = assignments.filter((a) => a.isActive);

  const renderContent = () => {
    if (loading) {
      return <Skeleton variant="text" width={260} height={20} />;
    }

    if (error) {
      return (
        <Typography variant="body2" sx={{ color: appColors.text.caption }}>
          Incident team could not be loaded.
        </Typography>
      );
    }

    const summary = formatTeamSummary(assignments);
    return (
      <Typography
        variant="body2"
        sx={{
          color: activeAssignments.length > 0 ? appColors.text.body : appColors.text.placeholder,
          fontStyle: activeAssignments.length > 0 ? 'normal' : 'italic',
        }}
      >
        {summary}
      </Typography>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        bgcolor: appColors.surface.card,
        border: `1px solid ${appColors.border.subtle}`,
        borderRadius: '12px',
        px: 1.5,
        py: 0.75,
        mb: 0.5,
        minHeight: 40,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        <TeamIcon
          aria-hidden
          sx={{ fontSize: 20, color: appColors.text.caption, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          component="span"
          sx={{ fontWeight: 700, color: appColors.text.heading, whiteSpace: 'nowrap', mr: 0.5 }}
        >
          Incident Team:
        </Typography>
        {renderContent()}
      </Box>

      <Button
        size="small"
        variant="outlined"
        onClick={onViewTeam}
        disabled={!!error}
        aria-label="View incident team details"
        sx={{
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: '8px',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        View Team
      </Button>
    </Box>
  );
};

export default IncidentTeamSummary;
